import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { useApp } from "../context/AppContext";
import { BRAND_SLUGS, BRANDS } from "../lib/brands";
import { CATEGORIES, catL10n, catPathFor } from "../lib/categories";
import { brandPath, lp } from "../lib/seo";
import Logo from "./Logo";
import { whatsappLink } from "../lib/contact";

const LogoLink = () => (
  <Link to={lp("/")} data-testid="logo-link" className="group flex items-center">
    <Logo />
  </Link>
);

export default function Header() {
  const { lang, currency, changeLang, changeCurrency, t } = useApp();
  const [open, setOpen] = useState(false);
  const [fleetExpanded, setFleetExpanded] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [open]);

  const closeMenu = () => {
    setOpen(false);
    setFleetExpanded(false);
  };

  const brandNavLabel = (slug) => {
    if (slug === "range-rover") {
      if (lang === "TR") {
        return "Range Rover Kiralama";
      }
      if (lang === "RU") {
        return "Аренда Range Rover";
      }
      return "Rent Range Rover";
    }
    return lang === "TR"
      ? `${BRANDS[slug].name} ${t.nav.rentPrefix}`
      : `${t.nav.rentPrefix} ${BRANDS[slug].name}`;
  };

  const goFleet = () => {
    closeMenu();
    if (window.location.pathname !== lp("/")) { navigate(lp("/")); setTimeout(() => document.getElementById("fleet")?.scrollIntoView({ behavior: "smooth" }), 300); }
    else document.getElementById("fleet")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-[#F4F1EB]/85 border-b border-black/10">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 h-[72px] flex items-center justify-between">
        <LogoLink />

        <nav className="hidden lg:flex items-center gap-8">
          <div className="relative group">
            <button data-testid="nav-fleet" className="text-sm text-slate-700 hover:text-teal-600 transition-colors duration-200 font-medium flex items-center gap-1">
              {t.nav.fleet} <ChevronDown size={14} className="group-hover:rotate-180 transition-transform duration-200" />
            </button>
            <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
              <div className="w-[500px] bg-white rounded-2xl shadow-xl border border-slate-200 p-4">
                <button onClick={goFleet} data-testid="nav-allcars" className="w-full text-left px-3 py-2.5 rounded-lg text-sm font-semibold text-slate-900 hover:bg-slate-50 transition-colors">{t.nav.allCars}</button>
                <div className="grid grid-cols-2 gap-1 mt-1 pb-3 mb-1 border-b border-slate-100">
                  {CATEGORIES.map((c) => (
                    <Link key={c.key} to={catPathFor(c)} onClick={() => setOpen(false)} data-testid={`nav-category-${c.key}`}
                      className="px-3 py-2 rounded-lg text-sm font-medium text-slate-800 hover:bg-teal-500/10 hover:text-teal-700 transition-colors">
                      {catL10n(c, lang).name}
                    </Link>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-1 mt-1">
                  {BRAND_SLUGS.map((s) => (
                    <Link key={s} to={brandPath(s)} onClick={() => setOpen(false)} data-testid={`nav-brand-${s}`}
                      className="px-3 py-2 rounded-lg text-sm text-slate-600 hover:bg-teal-500/10 hover:text-teal-700 transition-colors">
                      {brandNavLabel(s)}
                    </Link>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <button onClick={goFleet} data-testid="nav-services" className="text-sm text-slate-700 hover:text-teal-600 transition-colors duration-200 font-medium">{t.nav.services}</button>
          <a href="#about" data-testid="nav-about" className="text-sm text-slate-700 hover:text-teal-600 transition-colors duration-200 font-medium">{t.nav.about}</a>
          <a
            href={whatsappLink("Hi Silver Drive, I would like assistance with a rental.")}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-contact"
            data-link-placement="nav-contact"
            className="text-sm font-medium text-slate-700 transition-colors duration-200 hover:text-teal-600"
          >
            {t.nav.contact}
          </a>
        </nav>

        <div className="flex items-center gap-2 sm:gap-3">
          {/* Language */}
          <div className="hidden sm:flex items-center rounded-full border border-black/10 overflow-hidden">
            {["EN", "TR", "RU"].map((l) => (
              <button key={l} data-testid={`lang-${l.toLowerCase()}`} onClick={() => changeLang(l)}
                className={`px-2.5 py-1.5 text-xs font-mono-jb transition-colors duration-200 ${lang === l ? "bg-teal-500 text-[#042f2e] font-semibold" : "text-slate-600 hover:text-slate-900"}`}>{l}</button>
            ))}
          </div>
          {/* Currency */}
          <div className="hidden sm:flex items-center rounded-full border border-black/10 overflow-hidden">
            {["AED", "USD"].map((c) => (
              <button key={c} data-testid={`currency-${c.toLowerCase()}`} onClick={() => changeCurrency(c)}
                className={`px-3 py-1.5 text-xs font-mono-jb transition-colors duration-200 ${currency === c ? "bg-teal-500 text-[#042f2e] font-semibold" : "text-slate-600 hover:text-slate-900"}`}>{c}</button>
            ))}
          </div>
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen(!open)}
            aria-label={open ? "Close navigation" : "Open navigation"}
            aria-expanded={open}
            className="flex h-11 w-11 items-center justify-center text-slate-900 lg:hidden"
          >
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="max-h-[calc(100dvh-72px)] overflow-y-auto border-t border-white/10 bg-[#0E1017]/95 px-5 py-5 text-white backdrop-blur-2xl lg:hidden">
          <div className="flex flex-col gap-4">
            <div className="border-b border-white/10 pb-4">
              <button
                type="button"
                onClick={() => setFleetExpanded((value) => !value)}
                data-testid="mnav-fleet-accordion"
                aria-expanded={fleetExpanded}
                className="flex w-full items-center justify-between text-left font-medium text-white"
              >
                {t.nav.fleet}
                <ChevronDown
                  size={18}
                  className={`transition-transform duration-200 ${fleetExpanded ? "rotate-180" : ""}`}
                />
              </button>
              {fleetExpanded && (
                <div className="mt-4 space-y-5">
                  <button
                    type="button"
                    onClick={goFleet}
                    data-testid="mnav-allcars"
                    className="w-full border border-teal-300/30 bg-teal-400/10 px-4 py-3 text-left text-sm font-semibold text-teal-200 transition-colors duration-200 hover:bg-teal-400/20"
                  >
                    {t.nav.allCars}
                  </button>
                  <div className="grid grid-cols-2 gap-2">
                    {CATEGORIES.map((category) => (
                      <Link
                        key={category.key}
                        to={catPathFor(category)}
                        onClick={closeMenu}
                        data-testid={`mnav-category-${category.key}`}
                        className="border border-white/10 bg-white/5 px-3 py-3 text-sm font-medium text-slate-100 transition-colors duration-200 hover:border-teal-300/50 hover:bg-teal-400/10 hover:text-teal-200"
                      >
                        {catL10n(category, lang).name}
                      </Link>
                    ))}
                  </div>
                  <div className="grid grid-cols-2 gap-2 border-t border-white/10 pt-4">
                    {BRAND_SLUGS.map((slug) => (
                      <Link
                        key={slug}
                        to={brandPath(slug)}
                        onClick={closeMenu}
                        data-testid={`mnav-brand-${slug}`}
                        className="px-2 py-1 text-sm text-slate-300 transition-colors duration-200 hover:text-teal-200"
                      >
                        {brandNavLabel(slug)}
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
            <a href="#about" onClick={closeMenu} data-testid="mnav-about" className="font-medium text-slate-200">{t.nav.about}</a>
            <a
              href={whatsappLink("Hi Silver Drive, I would like assistance with a rental.")}
              target="_blank"
              rel="noopener noreferrer"
              onClick={closeMenu}
              data-testid="mnav-contact"
              className="min-h-12 text-left font-medium leading-[3rem] text-slate-200"
            >
              {t.nav.contact}
            </a>
          <div className="flex items-center gap-3 pt-2">
            <div className="flex items-center rounded-full border border-white/10 overflow-hidden">
              {["EN", "TR", "RU"].map((l) => (
                <button key={l} data-testid={`mnav-lang-${l.toLowerCase()}`} onClick={() => changeLang(l)} className={`px-3 py-1.5 text-xs font-mono-jb ${lang === l ? "bg-teal-300 text-slate-950" : "text-slate-400"}`}>{l}</button>
              ))}
            </div>
            <div className="flex items-center rounded-full border border-white/10 overflow-hidden">
              {["AED", "USD"].map((c) => (
                <button key={c} data-testid={`mnav-currency-${c.toLowerCase()}`} onClick={() => changeCurrency(c)} className={`px-3 py-1.5 text-xs font-mono-jb ${currency === c ? "bg-teal-300 text-slate-950" : "text-slate-400"}`}>{c}</button>
              ))}
            </div>
          </div>
          </div>
        </div>
      )}
    </header>
  );
}
